import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, EmptyState, PageContainer, Pagination, Skeleton } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
// Online orders carry their OWN currency, not the tenant's — an order may be
// placed on a storefront in a different one, so this must NOT use useTenantCurrency().
import { toCurrencyCode } from '@/hooks/use-tenant-currency';
import { siteOrderService } from '@/services/site-order.service';
import { slaRemaining, statusTone } from '@/lib/site-order-view';
import type { SiteOrder, SiteOrderListResponse } from '@/types/site-order';

/**
 * /online-orders — the owner's view of the storefront queue.
 *
 * Same API as the till, different job. The POS list is worked minute to minute
 * by whoever is standing at the counter; this one is where an owner sees what
 * nobody has touched, what was rejected and why, and how the week went.
 */

const PAGE_SIZE = 25;

type Scope = 'open' | 'sales' | 'all';

const SCOPES: Array<{ id: Scope; label: string }> = [
  { id: 'open', label: 'Needs action' },
  { id: 'sales', label: 'All orders' },
  { id: 'all', label: 'Orders & enquiries' },
];

const MODE_LABEL: Record<string, string> = {
  PICKUP: 'Collection',
  DELIVERY: 'Delivery',
  ENQUIRY: 'Enquiry',
};

export function OnlineOrderListPage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<Scope>('open');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<SiteOrderListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await siteOrderService.list({ scope, page, limit: PAGE_SIZE }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scope, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = result?.meta.counts ?? {};
  const orders = result?.data ?? [];

  return (
    <PageContainer
      title="Online orders"
      subtitle="Placed on your public website. Accepting one turns it into a draft bill."
    >
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Kpi
          label="Waiting"
          value={counts.NEW ?? 0}
          gradient="from-brand-500 to-indigo-500"
          hint="Nobody has accepted these yet"
        />
        <Kpi
          label="Accepted"
          value={counts.ACCEPTED ?? 0}
          gradient="from-violet-500 to-pink-500"
          hint="Draft bill created, not yet packed"
        />
        <Kpi
          label="Ready"
          value={counts.READY ?? 0}
          gradient="from-warning-500 to-danger-500"
          hint="Packed, waiting for the customer"
        />
        <Kpi
          label="Completed"
          value={counts.COMPLETED ?? 0}
          gradient="from-teal-500 to-success-500"
          hint="Paid and handed over"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setScope(s.id);
              setPage(1);
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ${
              scope === s.id
                ? 'bg-brand-600 text-white shadow-md'
                : 'bg-white text-neutral-600 hover:bg-brand-50 dark:bg-neutral-800 dark:text-neutral-300'
            }`}
          >
            {s.label}
          </button>
        ))}
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      {error && <Alert variant="danger" description={error} className="mb-4" />}

      {loading && !result ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          title={scope === 'open' ? 'Nothing waiting' : 'No online orders yet'}
          description={
            scope === 'open'
              ? 'Every online order has been dealt with.'
              : 'Orders placed on your website will appear here.'
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-100 bg-white shadow-md dark:border-neutral-700 dark:bg-neutral-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-700">
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">How</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Placed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <Row key={order.id} order={order} onOpen={() => navigate(`/online-orders/${order.id}`)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && result.meta.totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={result.meta.page}
            totalPages={result.meta.totalPages}
            totalItems={result.meta.total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </PageContainer>
  );
}

function Kpi({
  label,
  value,
  gradient,
  hint,
}: {
  label: string;
  value: number;
  gradient: string;
  hint: string;
}) {
  return (
    <div
      title={hint}
      className="rounded-xl border border-neutral-100 bg-white p-5 shadow-md transition-all duration-200 hover:shadow-lg dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div className="mb-3 flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${gradient} text-white`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
            <circle cx="12" cy="12" r="9" />
          </svg>
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">{value}</p>
    </div>
  );
}

function Row({ order, onOpen }: { order: SiteOrder; onOpen: () => void }) {
  const status = statusTone(order.status);
  const sla = slaRemaining(order);

  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer border-b border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-700 dark:even:bg-neutral-900/40 dark:hover:bg-neutral-700"
    >
      <td className="px-4 py-3 font-medium text-neutral-900 dark:text-white">
        {order.order_number}
      </td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
        {order.customer_name}
        <span className="block text-xs text-neutral-400">{order.customer_phone}</span>
      </td>
      <td className="px-4 py-3 text-neutral-600 dark:text-neutral-300">
        {MODE_LABEL[order.fulfilment_mode] ?? order.fulfilment_mode}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={status.tone}>{status.label}</Badge>
          {order.payment_status === 'PAID' && <Badge variant="success">Paid</Badge>}
          {/* Only for an order with a real deadline — today that means a
              prepaid one. A countdown on a pay-on-collection order would
              invent urgency where no money is waiting. */}
          {sla && (
            <Badge variant={sla.overdue ? 'danger' : 'warning'}>
              {sla.overdue ? `${sla.minutes}m over` : `${sla.minutes}m left`}
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-neutral-900 dark:text-white">
        {order.grand_total === null ? '—' : formatCurrency(order.grand_total, toCurrencyCode(order.currency))}
      </td>
      <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400">
        {new Date(order.created_at).toLocaleString()}
      </td>
    </tr>
  );
}
